import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';

const heroMovies = movies.filter((m) => m.isHot).slice(0, 4);

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 500);
    },
    [isTransitioning]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % heroMovies.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const movie = heroMovies[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {heroMovies.map((m, index) => (
        <div
          key={m.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={m.image}
            alt={m.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background-50"></div>

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 text-xs font-medium bg-primary-500 text-white rounded-full whitespace-nowrap">
                  {movie.quality}
                </span>
                <span className="px-3 py-1 text-xs font-medium bg-accent-500/80 text-background-950 rounded-full whitespace-nowrap">
                  热门推荐
                </span>
              </div>

              <h1
                className="text-3xl md:text-5xl font-bold text-white font-heading mb-3 transition-all duration-500"
                key={movie.id + '-title'}
              >
                {movie.title}
              </h1>

              <p
                className="text-sm md:text-base text-foreground-200/80 mb-2 leading-relaxed line-clamp-2 md:line-clamp-3 transition-all duration-500"
                key={movie.id + '-desc'}
              >
                {movie.description}
              </p>

              <div className="flex items-center gap-4 mb-6 text-sm text-foreground-300">
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-accent-500 text-xs"></i>
                  </div>
                  {movie.rating}
                </span>
                <span>{movie.year}</span>
                <span>{movie.duration}</span>
              </div>

              <div className="flex items-center gap-3">
                <Link
                  to={`/movie/${movie.id}`}
                  className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-play-fill"></i>
                  </div>
                  立即观看
                </Link>
                <Link
                  to={`/movie/${movie.id}`}
                  className="flex items-center gap-2 px-6 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full border border-white/20 transition-all duration-300 whitespace-nowrap"
                >
                  查看详情
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroMovies.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === current
                ? 'bg-primary-500 w-6'
                : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`切换到第${index + 1}张`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + heroMovies.length) % heroMovies.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-all duration-300 whitespace-nowrap"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={() => goTo((current + 1) % heroMovies.length)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-all duration-300 whitespace-nowrap"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>
    </section>
  );
}